import { useCallback, useEffect, useState } from 'react'
import { Alert, Layouts, Modal } from 'components'
import * as api from 'api'
import s from './styles.module.scss'

const categoryOptions = [
  'Spiritual',
  'Cultural',
  'Business Networking',
  'Career',
  'Workshop',
  'Family',
  'Youth',
  'Health & Wellness',
  'Community',
]

const defaultForm = {
  id: '',
  title: '',
  description: '',
  category: '',
  city: '',
  venue: '',
  locationUrl: '',
  startDateTime: '',
  endDateTime: '',
  coverImage: '',
  tagsInput: '',
  featured: false,
  isPublished: true,
  startingPrice: '',
  priceLabel: '',
  ctaLabel: 'Book Now',
  ctaUrl: '',
  organizerName: '',
}

const toDateTimeLocal = value => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = n => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const formatDateTime = value => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString()
}

const bookingStatusClass = status => {
  if (status === 'contacted') return s.statusContacted
  if (status === 'closed') return s.statusClosed
  return s.statusNew
}

export default function AdminEvents() {
  const [form, setForm] = useState(defaultForm)
  const [events, setEvents] = useState([])
  const [bookings, setBookings] = useState([])
  const [bookingUpdatingId, setBookingUpdatingId] = useState('')
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const isEditing = !!form.id

  const fetchEvents = useCallback(async () => {
    try {
      const response = await api.admin.events.fetchAll()
      if (response?.code === 200) setEvents(response.payload || [])
      else Alert.error(response?.message || 'Failed to load events')
    } catch {
      Alert.error('Failed to load events')
    }
  }, [])

  const fetchBookings = useCallback(async () => {
    try {
      const response = await api.admin.events.fetchBookings()
      if (response?.code === 200) setBookings(response.payload || [])
      else Alert.error(response?.message || 'Failed to load event bookings')
    } catch {
      Alert.error('Failed to load event bookings')
    }
  }, [])

  useEffect(() => {
    fetchEvents()
    fetchBookings()
  }, [fetchEvents, fetchBookings])

  const updateField = event => {
    const { name, value, type, checked } = event.target
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const resetForm = () => setForm(defaultForm)

  const handleImageUpload = async event => {
    const file = event.target.files?.[0]
    if (!file) return
    const formData = new FormData()
    formData.append('coverImage', file)
    setUploading(true)
    try {
      const response = await api.admin.events.uploadImage(formData)
      if (response?.code === 200 && response?.payload?.url) {
        setForm(prev => ({ ...prev, coverImage: response.payload.url }))
        Alert.success('Image uploaded successfully.')
      } else {
        Alert.error(response?.message || 'Failed to upload image')
      }
    } catch (error) {
      Alert.error(error?.response?.data?.message || 'Failed to upload image')
    } finally {
      setUploading(false)
      event.target.value = ''
    }
  }

  const handleSubmit = async event => {
    event.preventDefault()
    if (!form.title.trim() || !form.city.trim() || !form.category.trim() || !form.startDateTime) {
      Alert.warn('Please fill all required fields: title, city, category and start date/time.')
      return
    }
    if (form.endDateTime && form.startDateTime && new Date(form.endDateTime) < new Date(form.startDateTime)) {
      Alert.warn('End date/time cannot be earlier than start date/time.')
      return
    }
    const normalizedPrice = Number(form.startingPrice || 0)
    const resolvedPriceLabel =
      form.priceLabel.trim() || (normalizedPrice <= 0 ? 'Free Entry' : '')

    const payload = {
      ...(form.id ? { id: form.id } : {}),
      title: form.title.trim(),
      description: form.description.trim(),
      category: form.category.trim() || 'General',
      city: form.city.trim(),
      venue: form.venue.trim(),
      locationUrl: form.locationUrl.trim(),
      startDateTime: new Date(form.startDateTime).toISOString(),
      endDateTime: form.endDateTime ? new Date(form.endDateTime).toISOString() : null,
      coverImage: form.coverImage.trim(),
      tags: form.tagsInput
        .split(',')
        .map(item => item.trim())
        .filter(Boolean),
      featured: form.featured,
      isPublished: form.isPublished,
      startingPrice: normalizedPrice,
      priceLabel: resolvedPriceLabel,
      ctaLabel: form.ctaLabel.trim() || 'Book Now',
      ctaUrl: form.ctaUrl.trim(),
      organizerName: form.organizerName.trim(),
    }
    setLoading(true)
    try {
      const response = isEditing ? await api.admin.events.update(payload) : await api.admin.events.create(payload)
      if (response?.code === 200 || response?.code === 201) {
        Alert.success(isEditing ? 'Event updated successfully.' : 'Event created successfully.')
        resetForm()
        fetchEvents()
        fetchBookings()
      } else {
        const message =
          response?.message ||
          response?.payload?.message ||
          response?.error ||
          'Failed to save event'
        Alert.error(message)
        console.error('Events save API rejected response:', response)
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to save event'
      Alert.error(message)
      console.error('Events save request failed:', {
        message,
        status: error?.response?.status,
        data: error?.response?.data,
        error,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = eventData => {
    setForm({
      id: eventData._id,
      title: eventData.title || '',
      description: eventData.description || '',
      category: eventData.category || '',
      city: eventData.city || '',
      venue: eventData.venue || '',
      locationUrl: eventData.locationUrl || '',
      startDateTime: toDateTimeLocal(eventData.startDateTime),
      endDateTime: toDateTimeLocal(eventData.endDateTime),
      coverImage: eventData.coverImage || '',
      tagsInput: (eventData.tags || []).join(', '),
      featured: !!eventData.featured,
      isPublished: eventData.isPublished !== false,
      startingPrice: eventData.startingPrice || '',
      priceLabel: eventData.priceLabel || '',
      ctaLabel: eventData.ctaLabel || 'Book Now',
      ctaUrl: eventData.ctaUrl || '',
      organizerName: eventData.organizerName || '',
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDelete = id => {
    Modal.Confirm('Delete this event?', async () => {
      try {
        const response = await api.admin.events.delete(id)
        if (response?.code === 200) {
          Alert.success('Event deleted.')
          fetchEvents()
          fetchBookings()
        } else {
          Alert.error(response?.message || 'Failed to delete event')
          console.error('Events delete API rejected response:', response)
        }
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to delete event'
        Alert.error(message)
        console.error('Events delete request failed:', error)
      }
    })
  }

  const handleBookingStatusChange = async (bookingId, status) => {
    if (!bookingId || !status) return
    setBookingUpdatingId(bookingId)
    try {
      const response = await api.admin.events.updateBookingStatus({ bookingId, status })
      if (response?.code === 200) {
        setBookings(prev => prev.map(item => (item._id === bookingId ? { ...item, status } : item)))
        Alert.success('Booking status updated.')
      } else {
        Alert.error(response?.message || 'Failed to update booking status')
      }
    } catch (error) {
      Alert.error(error?.response?.data?.message || 'Failed to update booking status')
    } finally {
      setBookingUpdatingId('')
    }
  }

  const handleBookingApprovalChange = async (bookingId, approvalStatus) => {
    if (!bookingId || !approvalStatus) return
    setBookingUpdatingId(bookingId)
    try {
      const response = await api.admin.events.updateBookingApproval({ bookingId, approvalStatus })
      if (response?.code === 200) {
        setBookings(prev => prev.map(item => (item._id === bookingId ? { ...item, approvalStatus } : item)))
        Alert.success('Booking approval updated.')
      } else {
        Alert.error(response?.message || 'Failed to update booking approval')
      }
    } catch (error) {
      Alert.error(error?.response?.data?.message || 'Failed to update booking approval')
    } finally {
      setBookingUpdatingId('')
    }
  }

  return (
    <div className={s.wrap}>
      <Layouts.Classic title='Events' />
      <div className={s.pageTitle}>BCN Events Management</div>
      <p className={s.pageHelp}>
        Add events in 4 simple steps: <strong>Basic details</strong>, <strong>Date & venue</strong>, <strong>Pricing</strong>, and <strong>Publishing</strong>.
      </p>
      <form className={s.form} onSubmit={handleSubmit}>
        <div className={s.sectionTitle}>1) Basic Details</div>
        <div className={s.grid}>
          <div className={s.field}>
            <label>Event Title *</label>
            <input name='title' value={form.title} onChange={updateField} placeholder='e.g. BCN Startup Mixer 2026' required />
          </div>
          <div className={s.field}>
            <label>Category *</label>
            <select name='category' value={form.category} onChange={updateField} required>
              <option value=''>Select category</option>
              {categoryOptions.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div className={s.field}>
            <label>Organizer Name</label>
            <input name='organizerName' value={form.organizerName} onChange={updateField} placeholder='e.g. BCN Bangalore Team' />
          </div>
          <div className={s.field}>
            <label>Event Tags</label>
            <input name='tagsInput' value={form.tagsInput} onChange={updateField} placeholder='e.g. networking, founders, youth' />
          </div>
        </div>

        <div className={s.field}>
          <label>Description</label>
          <textarea
            name='description'
            value={form.description}
            onChange={updateField}
            placeholder='What is this event about? Who should attend?'
            rows={4}
          />
        </div>

        <div className={s.sectionTitle}>2) Date, Time & Venue</div>
        <div className={s.grid}>
          <div className={s.field}>
            <label>City *</label>
            <input name='city' value={form.city} onChange={updateField} placeholder='e.g. Delhi' required />
          </div>
          <div className={s.field}>
            <label>Venue</label>
            <input name='venue' value={form.venue} onChange={updateField} placeholder='e.g. Community Hall, Dwarka' />
          </div>
          <div className={s.field}>
            <label>Google Maps Link</label>
            <input name='locationUrl' value={form.locationUrl} onChange={updateField} placeholder='https://maps.google.com/...' />
          </div>
          <div className={s.field}>
            <label>Start Date & Time *</label>
            <input type='datetime-local' name='startDateTime' value={form.startDateTime} onChange={updateField} required />
          </div>
          <div className={s.field}>
            <label>End Date & Time</label>
            <input type='datetime-local' name='endDateTime' value={form.endDateTime} onChange={updateField} />
          </div>
        </div>

        <div className={s.sectionTitle}>3) Price & Booking</div>
        <div className={s.grid}>
          <div className={s.field}>
            <label>Starting Price</label>
            <input type='number' min='0' name='startingPrice' value={form.startingPrice} onChange={updateField} placeholder='e.g. 299' />
            <small className={s.helperText}>Set 0 for free events</small>
          </div>
          <div className={s.field}>
            <label>Price Label</label>
            <input name='priceLabel' value={form.priceLabel} onChange={updateField} placeholder='e.g. Free Entry / From Rs 299' />
          </div>
          <div className={s.field}>
            <label>Button Text</label>
            <input name='ctaLabel' value={form.ctaLabel} onChange={updateField} placeholder='e.g. Register Now' />
          </div>
          <div className={s.field}>
            <label>Booking Link (URL)</label>
            <input name='ctaUrl' value={form.ctaUrl} onChange={updateField} placeholder='https://...' />
          </div>
        </div>

        <div className={s.sectionTitle}>4) Media & Visibility</div>
        <div className={s.grid}>
          <div className={s.field}>
            <label>Upload Cover Image</label>
            <input type='file' accept='image/*' onChange={handleImageUpload} disabled={uploading} />
            {uploading && <small className={s.helperText}>Uploading...</small>}
          </div>
          <div className={s.field}>
            <label>Or Paste Image URL</label>
            <input name='coverImage' value={form.coverImage} onChange={updateField} placeholder='https://image-url...' />
          </div>
        </div>
        {form.coverImage && (
          <div className={s.imagePreview}>
            <img src={form.coverImage} alt='Cover preview' />
          </div>
        )}

        <div className={s.switches}>
          <label><input type='checkbox' name='featured' checked={form.featured} onChange={updateField} /> Show as Featured Event</label>
          <label><input type='checkbox' name='isPublished' checked={form.isPublished} onChange={updateField} /> Publish on Frontend</label>
        </div>
        <div className={s.actions}>
          <button type='submit' disabled={loading}>{loading ? 'Saving...' : isEditing ? 'Update Event' : 'Save Event'}</button>
          {isEditing ? (
            <button type='button' onClick={resetForm} className={s.secondary}>Cancel Edit</button>
          ) : null}
        </div>
      </form>

      <div className={s.tableWrap}>
        <h3>All Events</h3>
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>City</th>
              <th>Category</th>
              <th>Start</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map(item => (
              <tr key={item._id}>
                <td>{item.title}</td>
                <td>{item.city}</td>
                <td>{item.category || '-'}</td>
                <td>{formatDateTime(item.startDateTime)}</td>
                <td><span className={item.isPublished ? s.badgePublished : s.badgeDraft}>{item.isPublished ? 'Published' : 'Draft'}</span></td>
                <td>
                  <button type='button' onClick={() => handleEdit(item)} className={s.linkBtn}>Edit</button>
                  <button type='button' onClick={() => handleDelete(item._id)} className={s.linkBtnDelete}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={s.tableWrap}>
        <h3>Booking Requests</h3>
        <table>
          <thead>
            <tr>
              <th>Event</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>City</th>
              <th>Status</th>
              <th>Approval</th>
              <th>Requested On</th>
            </tr>
          </thead>
          <tbody>
            {bookings.length ? (
              bookings.map(item => (
                <tr key={item._id}>
                  <td>{item.eventTitle || '-'}</td>
                  <td>{item.viewerName || '-'}</td>
                  <td>{item.viewerPhone || '-'}</td>
                  <td>{item.viewerEmail || '-'}</td>
                  <td>{[item.viewerCity, item.viewerState].filter(Boolean).join(', ') || '-'}</td>
                  <td>
                    <select
                      className={`${s.statusSelect} ${bookingStatusClass(item.status || 'new')}`}
                      value={item.status || 'new'}
                      onChange={event => handleBookingStatusChange(item._id, event.target.value)}
                      disabled={bookingUpdatingId === item._id}
                    >
                      <option value='new'>new</option>
                      <option value='contacted'>contacted</option>
                      <option value='closed'>closed</option>
                    </select>
                  </td>
                  <td>
                    <select
                      className={s.statusSelect}
                      value={item.approvalStatus || 'pending'}
                      onChange={event => handleBookingApprovalChange(item._id, event.target.value)}
                      disabled={bookingUpdatingId === item._id}
                    >
                      <option value='pending'>pending</option>
                      <option value='approved'>approved</option>
                      <option value='rejected'>rejected</option>
                    </select>
                  </td>
                  <td>{formatDateTime(item.createdAt)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan='8'>No booking requests yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
